import type { ContentBundle, RSVP, ClientPrincipal, Session, Speaker, Sponsor } from './types';

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${detail}`);
  }
  return res.json() as Promise<T>;
}

export async function getContent(includeUnpublished = false): Promise<ContentBundle> {
  const q = includeUnpublished ? '?type=all&all=1' : '?type=all';
  return handle<ContentBundle>(await fetch(`/api/content${q}`));
}

type ContentType = 'sessions' | 'speakers' | 'sponsors';

export async function saveContent(type: ContentType, item: Partial<Session | Speaker | Sponsor>) {
  return handle(await fetch(`/api/content-save?type=${type}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(item)
  }));
}

export async function deleteContent(type: ContentType, id: string) {
  return handle(await fetch(`/api/content-delete?type=${type}&id=${encodeURIComponent(id)}`, { method: 'POST' }));
}

export async function submitRsvp(payload: { sessionIds: string[]; dietaryRestrictions?: string; accessibilityNeeds?: string; }) {
  return handle<{ registered: number; alreadyRegistered: number }>(
    await fetch('/api/rsvp', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
  );
}

export async function getRsvpList(): Promise<{ total: number; items: RSVP[] }> {
  return handle(await fetch('/api/rsvp-list'));
}

export async function getUser(): Promise<ClientPrincipal | null> {
  const res = await fetch('/.auth/me');
  if (!res.ok) return null;
  const data = await res.json();
  return data.clientPrincipal ?? null;
}

// Admin management ----------------------------------------------------------
export type AdminRecord = {
  email: string;
  role: 'admin' | 'superadmin';
  addedBy?: string;
  addedAt?: string;
};
export type AdminsBundle = { admins: AdminRecord[] };

export async function listAdmins(): Promise<AdminsBundle> {
  return handle(await fetch('/api/manage-admins'));
}
export async function saveAdmin(email: string, role: 'admin' | 'superadmin' = 'admin'): Promise<AdminsBundle> {
  return handle(await fetch('/api/manage-admins-save', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role })
  }));
}
export async function deleteAdmin(email: string): Promise<AdminsBundle> {
  return handle(await fetch('/api/manage-admins-delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  }));
}
